import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../axiosInstance";
import { createPortal } from "react-dom";

// ─── Status helpers ───────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PENDING: {
    label: "Pending",
    color: "#6B7280",
    bg: "#F3F4F6",
    dot: "#9CA3AF",
  },

  PROCESSING: {
    label: "Processing",
    color: "#92400E",
    bg: "#FEF3C7",
    dot: "#F59E0B",
  },

  IN_PROGRESS: {
    label: "In Progress",
    color: "#92400E",
    bg: "#FEF3C7",
    dot: "#F59E0B",
  },

  COMPLETED: {
    label: "Completed",
    color: "#065F46",
    bg: "#D1FAE5",
    dot: "#10B981",
  },

  FAILED: {
    label: "Failed",
    color: "#991B1B",
    bg: "#FEE2E2",
    dot: "#EF4444",
  },

  CANCELLED: {
    label: "Cancelled",
    color: "#991B1B",
    bg: "#FEE2E2",
    dot: "#EF4444",
  },

  PASSED: {
    label: "Passed",
    color: "#065F46",
    bg: "#D1FAE5",
    dot: "#10B981",
  },

  PAID: {
    label: "Paid",
    color: "#065F46",
    bg: "#D1FAE5",
    dot: "#10B981",
  },

  UNPAID: {
    label: "Unpaid",
    color: "#991B1B",
    bg: "#FEE2E2",
    dot: "#EF4444",
  },
};

const STAGE_TYPE_COLORS = {
  INITIAL: { bg: "#EDE9FE", color: "#5B21B6", border: "#C4B5FD" },
  INTERMEDIATE: { bg: "#DBEAFE", color: "#1E40AF", border: "#93C5FD" },
  FINAL: { bg: "#D1FAE5", color: "#065F46", border: "#6EE7B7" },
  WON: { bg: "#D1FAE5", color: "#065F46", border: "#6EE7B7" },
  LOST: { bg: "#FEE2E2", color: "#991B1B", border: "#FCA5A5" },
};

const ENQUIRY_TYPE_COLORS = {
  HOT: { bg: "#FEE2E2", color: "#991B1B" },
  WARM: { bg: "#FEF3C7", color: "#92400E" },
  COLD: { bg: "#DBEAFE", color: "#1E40AF" },
};

const PRIORITY_COLORS = {
  HIGH: { bg: "#FEE2E2", color: "#991B1B" },
  MEDIUM: { bg: "#FEF3C7", color: "#92400E" },
  LOW: { bg: "#D1FAE5", color: "#065F46" },
};

const Badge = ({ label, bg, color, border }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.03em",
      padding: "2px 8px",
      borderRadius: 20,
      background: bg,
      color,
      border: `1px solid ${border || bg}`,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

const StatusBadge = ({ status }) => {
  const normalizedStatus = status?.toUpperCase();

  const cfg = STATUS_CONFIG[normalizedStatus] || {
    label: status,
    color: "#374151",
    bg: "#F3F4F6",
    dot: "#9CA3AF",
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 20,
        background: cfg.bg,
        color: cfg.color,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
};

const formatDate = (d) => {
  if (!d) return null;
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(d));
};

const formatDateShort = (d) => {
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(d));
};

// ─── Section wrapper ──────────────────────────────────────────────────────────
const Section = ({ title, icon, children, accent = "#6366F1" }) => (
  <div style={{ marginBottom: 24 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: accent + "18",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          color: accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#6B7280",
          letterSpacing: "0.08em",
        }}
      >
        {title}
      </span>
    </div>
    {children}
  </div>
);

const InfoRow = ({ label, value }) =>
  value ? (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: "7px 0",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <span
        style={{
          fontSize: 13,
          color: "#9CA3AF",
          flexShrink: 0,
          marginRight: 12,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: "#111827",
          fontWeight: 500,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  ) : null;

// ─── Operation stage mini-timeline ───────────────────────────────────────────
const OpStages = ({ stages }) => {
  if (!stages?.length) return null;
  return (
    <div style={{ marginTop: 10 }}>
      {stages.map((op, i) => {
        const isLast = i === stages.length - 1;
        const status = op.status?.toUpperCase();

        const isDone = status === "COMPLETED";
        const isProcessing = status === "PROCESSING";
        const isCurrent =
          !isDone && (stages[i - 1]?.status === "COMPLETED" || i === 0);

        const dotColor = isDone
          ? "#10B981"
          : isProcessing
            ? "#F59E0B"
            : "#D1D5DB";

        return (
          <div
            key={op.operationStageHistoryId || i}
            style={{ display: "flex", gap: 12 }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  marginTop: 3,
                  background: dotColor,
                  boxShadow: isCurrent ? `0 0 0 3px ${dotColor}30` : "none",
                  flexShrink: 0,
                }}
              />
              {!isLast && (
                <div
                  style={{
                    width: 1,
                    flex: 1,
                    background: isDone ? "#10B981" : "#E5E7EB",
                    marginTop: 2,
                  }}
                />
              )}
            </div>
            <div style={{ paddingBottom: isLast ? 0 : 10, flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
                >
                  {op.operationStageName}
                </span>
                <StatusBadge status={op.status} />
                {op.qcStatus && <StatusBadge status={op.qcStatus} />}
              </div>
              {op.assignedEmployeeName && (
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                  👤 {op.assignedEmployeeName}
                </div>
              )}
              {op.operationStartedAt && (
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                  {formatDate(op.operationStartedAt)}
                  {op.operationEndedAt &&
                    ` → ${formatDate(op.operationEndedAt)}`}
                </div>
              )}
              {op.remarks && (
                <div
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                    marginTop: 2,
                    fontStyle: "italic",
                  }}
                >
                  {op.remarks}
                </div>
              )}
              {op.returned && (
                <div style={{ fontSize: 11, color: "#EF4444", marginTop: 2 }}>
                  ↩ Returned: {op.returnedMessage}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Product card inside purchase order ──────────────────────────────────────
const PurchaseProduct = ({ product }) => {
  const [open, setOpen] = useState(false);
  const hasStages = product.operationStages?.length > 0;
  const doneStages =
    product.operationStages?.filter((s) => s.status === "COMPLETED").length ||
    0;
  const totalStages = product.operationStages?.length || 0;
  const pct =
    totalStages > 0 ? Math.round((doneStages / totalStages) * 100) : 0;

  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        overflow: "hidden",
        marginBottom: 8,
        background: "#FAFAFA",
      }}
    >
      <div
        onClick={() => hasStages && setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          cursor: hasStages ? "pointer" : "default",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#111827",
              marginBottom: 2,
            }}
          >
            {product.productName}
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>
              Qty: {product.quantity}
            </span>
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>
              ₹{product.price?.toLocaleString("en-IN")}
            </span>
            {product.managerApprovalStatus && (
              <StatusBadge status={product.managerApprovalStatus} />
            )}
          </div>
          {totalStages > 0 && (
            <div style={{ marginTop: 6 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 10,
                  color: "#9CA3AF",
                  marginBottom: 2,
                }}
              >
                <span>Production progress</span>
                <span>
                  {doneStages}/{totalStages} stages
                </span>
              </div>
              <div
                style={{
                  height: 4,
                  background: "#E5E7EB",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: pct === 100 ? "#10B981" : "#6366F1",
                    borderRadius: 2,
                    transition: "width 0.4s",
                  }}
                />
              </div>
            </div>
          )}
        </div>
        {hasStages && (
          <span
            style={{
              fontSize: 16,
              color: "#9CA3AF",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
              flexShrink: 0,
            }}
          >
            ▾
          </span>
        )}
      </div>
      {open && hasStages && (
        <div
          style={{
            padding: "0 12px 12px 12px",
            borderTop: "1px solid #F3F4F6",
          }}
        >
          <OpStages stages={product.operationStages} />
        </div>
      )}
    </div>
  );
};

// ─── Enquiry Stage History item ───────────────────────────────────────────────
const StageHistoryItem = ({ stage, isActive, isLast, index }) => {
  const [open, setOpen] = useState(isActive);
  const hasProducts = stage.productEnquiryHistories?.length > 0;
  const typeColors =
    STAGE_TYPE_COLORS[stage.stageType] || STAGE_TYPE_COLORS.INTERMEDIATE;
  const status = stage.enquiryStatus?.toUpperCase();

  const dotColor =
    status === "COMPLETED"
      ? "#10B981"
      : status === "PROCESSING"
        ? "#F59E0B"
        : "#D1D5DB";
  return (
    <div style={{ display: "flex", gap: 14 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: dotColor,
            border: `2px solid ${isActive ? "#6366F1" : dotColor}`,
            boxShadow: isActive ? "0 0 0 4px #6366F120" : "none",
            marginTop: 2,
            flexShrink: 0,
            position: "relative",
            zIndex: 1,
          }}
        />
        {!isLast && (
          <div
            style={{
              width: 2,
              flex: 1,
              background:
                stage.enquiryStatus === "COMPLETED" ? "#10B981" : "#E5E7EB",
              marginTop: 4,
            }}
          />
        )}
      </div>

      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 20 }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            cursor: "pointer",
            gap: 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                marginBottom: 4,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                {stage.stageName}
              </span>
              <Badge label={stage.stageType} {...typeColors} />
              {stage.enquiryStatus && (
                <StatusBadge status={stage.enquiryStatus} />
              )}
            </div>
            {stage.startedAt && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 4,
                  padding: "4px 10px",
                  borderRadius: 8,
                  background: "#EEF2FF",
                  color: "#4338CA",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                📅 {formatDate(stage.startedAt)}
                {stage.endedAt && ` → ${formatDate(stage.endedAt)}`}
              </div>
            )}
          </div>
          <span
            style={{
              fontSize: 14,
              color: "#9CA3AF",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            ▾
          </span>
        </div>

        {open && (
          <div style={{ marginTop: 10 }}>
            {stage.assignedTo && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#F9FAFB",
                  borderRadius: 8,
                  padding: "8px 10px",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#E0E7FF",
                    color: "#4338CA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {stage.assignedTo
                    .split(" ")
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </div>
                <div>
                  <div
                    style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}
                  >
                    {stage.assignedTo}
                  </div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                    Assigned rep
                  </div>
                </div>
              </div>
            )}

            {stage.dueAt && (
              <div
                style={{
                  fontSize: 12,
                  color: "#EF4444",
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                ⏰ Due: {formatDate(stage.dueAt)}
              </div>
            )}

            {stage.remarks && (
              <div
                style={{
                  fontSize: 12,
                  color: "#6B7280",
                  fontStyle: "italic",
                  background: "#F9FAFB",
                  borderRadius: 6,
                  padding: "6px 10px",
                  marginBottom: 8,
                  borderLeft: "3px solid #E5E7EB",
                }}
              >
                "{stage.remarks}"
              </div>
            )}

            {stage.enquiryStageCheckListHistories?.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    letterSpacing: "0.06em",
                    marginBottom: 6,
                  }}
                >
                  Checklist ({stage.enquiryStageCheckListHistories.length})
                </div>
                {stage.enquiryStageCheckListHistories.map((c, i) => (
                  <div
                    key={c.id ?? i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      background: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      padding: "8px 10px",
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        lineHeight: "16px",
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      {c.completed ? "✅" : "⬜"}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {c.checklistName ? (
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#374151",
                          }}
                        >
                          {c.checklistName}
                        </div>
                      ) : null}
                      {c.remarks ? (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6B7280",
                            fontStyle: "italic",
                            marginTop: c.checklistName ? 2 : 0,
                          }}
                        >
                          "{c.remarks}"
                        </div>
                      ) : (
                        !c.checklistName && (
                          <div style={{ fontSize: 11, color: "#D1D5DB" }}>
                            No remarks
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hasProducts && (
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    letterSpacing: "0.06em",

                    marginBottom: 6,
                  }}
                >
                  Products ({stage.productEnquiryHistories.length})
                </div>
                {stage.productEnquiryHistories.map((p, i) => (
                  <div
                    key={p.id || i}
                    style={{
                      background: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      padding: "8px 10px",
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#374151",
                          }}
                        >
                          {p.productName}
                        </div>
                        {p.combinationDetails?.skuCode && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "#9CA3AF",
                              fontFamily: "monospace",
                            }}
                          >
                            SKU: {p.combinationDetails.skuCode}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#111827",
                          }}
                        >
                          ₹{p.price?.toLocaleString("en-IN")}
                        </div>
                        <div style={{ fontSize: 10, color: "#9CA3AF" }}>
                          Qty: {p.quantity}
                        </div>
                        {p.fulfillmentStatus && (
                          <StatusBadge status={p.fulfillmentStatus} />
                        )}
                      </div>
                    </div>
                    {p.specifications?.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          flexWrap: "wrap",
                          marginTop: 6,
                        }}
                      >
                        {p.specifications.map((s, si) => (
                          <span
                            key={si}
                            style={{
                              fontSize: 10,
                              padding: "1px 6px",
                              borderRadius: 4,
                              background: "#EDE9FE",
                              color: "#5B21B6",
                            }}
                          >
                            {s.specificationName}: {s.specificationValue}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Purchase Order card ──────────────────────────────────────────────────────
const PurchaseOrderCard = ({ po }) => {
  const [open, setOpen] = useState(false);
  const hasProducts = po.products?.length > 0;

  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 10,
        background: "#fff",
      }}
    >
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 14px",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 9,
            background: "#FEF3C7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          🧾
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
              {po.orderNumber || `PO-${po.purchaseOrderId}`}
            </span>
            <StatusBadge status={po.orderStatus} />
            <StatusBadge status={po.paymentStatus} />
          </div>
          <div
            style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap" }}
          >
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>
              ₹{po.totalAmount?.toLocaleString("en-IN")}
            </span>
            {po.orderDate && (
              <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                📅 {formatDateShort(po.orderDate)}
              </span>
            )}
            {po.expectedDeliveryDate && (
              <span style={{ fontSize: 11, color: "#9CA3AF" }}>
                🚚 {formatDateShort(po.expectedDeliveryDate)}
              </span>
            )}
          </div>
        </div>
        {hasProducts && (
          <span
            style={{
              fontSize: 14,
              color: "#9CA3AF",
              transform: open ? "rotate(180deg)" : "none",
              transition: "transform 0.2s",
              marginTop: 2,
            }}
          >
            ▾
          </span>
        )}
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #F3F4F6", padding: "12px 14px" }}>
          {po.deliveryAddress && (
            <InfoRow label="Delivery" value={po.deliveryAddress} />
          )}
          {po.billingAddress && (
            <InfoRow label="Billing" value={po.billingAddress} />
          )}
          {po.paidAmount != null && po.paidAmount > 0 && (
            <InfoRow
              label="Paid"
              value={`₹${po.paidAmount.toLocaleString("en-IN")}`}
            />
          )}
          {po.paidDate && (
            <InfoRow label="Paid on" value={formatDateShort(po.paidDate)} />
          )}

          {hasProducts && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9CA3AF",
                  letterSpacing: "0.06em",

                  marginBottom: 8,
                }}
              >
                Products & Production
              </div>
              {po.products.map((prod, i) => (
                <PurchaseProduct
                  key={prod.productOrderHistoryId || i}
                  product={prod}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Sidebar ─────────────────────────────────────────────────────────────
const EnquiryTimelineSidebar = ({ enquiryId, getApiEndpoint, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("timeline");
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!enquiryId) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await axiosInstance.get(
          getApiEndpoint(`/sales-enquiry/${enquiryId}`),
        );
        setData(res.data?.data || null);
      } catch (e) {
        setError("Failed to load enquiry details.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [enquiryId, getApiEndpoint]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };

  const enquiry = data;
  const stageHistories = enquiry?.stageHistories || [];
  const purchaseOrders = enquiry?.purchaseOrders || [];
  const currentStageIndex = stageHistories.findLastIndex(
    (s) =>
      s.enquiryStatus === "PROCESSING" || s.enquiryStatus === "IN_PROGRESS",
  );

  const TABS = [
    { id: "timeline", label: "Timeline", count: stageHistories.length },
    { id: "orders", label: "Orders", count: purchaseOrders.length },
    { id: "overview", label: "Overview", count: null },
  ];

  const totalOrderValue = purchaseOrders.reduce(
    (s, p) => s + (p.totalAmount || 0),
    0,
  );
  const allOpStages = purchaseOrders.flatMap(
    (po) => po.products?.flatMap((p) => p.operationStages || []) || [],
  );
  const doneOps = allOpStages.filter((s) => s.status === "COMPLETED").length;
  const totalOps = allOpStages.length;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        justifyContent: "flex-end",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        style={{
          width: "min(520px, 96vw)",
          height: "100%",
          background: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.15)",
          animation: "slideIn 0.28s cubic-bezier(0.34,1.06,0.64,1)",
          overflowY: "hidden",
        }}
      >
        <style>{`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0.5; }
            to   { transform: translateX(0);    opacity: 1;   }
          }
        `}</style>

        {/* ── Header ── */}
        <div
          style={{
            padding: "18px 20px 0",
            borderBottom: "1px solid #F3F4F6",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: "#111827",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Enquiry Track
                </span>
                {enquiry?.enquiryCode && (
                  <span
                    style={{
                      fontSize: 11,
                      fontFamily: "monospace",
                      fontWeight: 700,
                      background: "#F3F4F6",
                      color: "#6B7280",
                      padding: "2px 7px",
                      borderRadius: 5,
                    }}
                  >
                    {enquiry.enquiryCode}
                  </span>
                )}
              </div>
              {enquiry && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {enquiry.type && (
                    <Badge
                      label={enquiry.type}
                      {...(ENQUIRY_TYPE_COLORS[enquiry.type] || {
                        bg: "#F3F4F6",
                        color: "#374151",
                      })}
                    />
                  )}
                  {enquiry.priority && (
                    <Badge
                      label={enquiry.priority}
                      {...(PRIORITY_COLORS[enquiry.priority] || {
                        bg: "#F3F4F6",
                        color: "#374151",
                      })}
                    />
                  )}
                  {enquiry.status && <StatusBadge status={enquiry.status} />}
                  {enquiry.currentStageName && (
                    <Badge
                      label={`📍 ${enquiry.currentStageName}`}
                      bg="#F0FDF4"
                      color="#166534"
                    />
                  )}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                width: 32,
                height: 32,
                border: "none",
                background: "#F3F4F6",
                borderRadius: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#6B7280",
                fontSize: "18px",
                fontWeight: "bold",
              }}
            >
              ×
            </button>
          </div>

          {/* Summary stats */}
          {enquiry && !loading && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[
                {
                  label: "Stage Value",
                  value: `₹${(enquiry.totalEstimateAmount || 0).toLocaleString("en-IN")}`,
                },
                {
                  label: "Quotation",
                  value: `₹${(enquiry.totalQuotationAmount || 0).toLocaleString("en-IN")}`,
                },
                totalOps > 0
                  ? {
                      label: "Production",
                      value: `${doneOps}/${totalOps} done`,
                    }
                  : null,
              ]
                .filter(Boolean)
                .map((s, i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      background: "#F9FAFB",
                      borderRadius: 8,
                      padding: "7px 10px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: "#9CA3AF",
                        marginBottom: 2,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.label}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      {s.value}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", gap: 2 }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: "8px 4px",
                  border: "none",
                  background: "transparent",
                  fontSize: 13,
                  fontWeight: activeTab === tab.id ? 700 : 500,
                  color: activeTab === tab.id ? "#6366F1" : "#9CA3AF",
                  borderBottom: `2px solid ${activeTab === tab.id ? "#6366F1" : "transparent"}`,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 5,
                }}
              >
                {tab.label}
                {tab.count != null && (
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      background: activeTab === tab.id ? "#EDE9FE" : "#F3F4F6",
                      color: activeTab === tab.id ? "#6366F1" : "#9CA3AF",
                      padding: "1px 5px",
                      borderRadius: 10,
                    }}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          {loading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingTop: 60,
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  border: "3px solid #E5E7EB",
                  borderTopColor: "#6366F1",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                }}
              />
              <span style={{ fontSize: 13, color: "#9CA3AF" }}>
                Loading enquiry details…
              </span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {error && !loading && (
            <div
              style={{
                textAlign: "center",
                paddingTop: 60,
                color: "#EF4444",
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && !enquiry && (
            <div
              style={{
                textAlign: "center",
                paddingTop: 60,
                color: "#9CA3AF",
                fontSize: 14,
              }}
            >
              No data found for this enquiry.
            </div>
          )}

          {!loading && enquiry && (
            <>
              {/* ─── TIMELINE TAB ─── */}
              {activeTab === "timeline" && (
                <div>
                  {stageHistories.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        paddingTop: 40,
                        color: "#9CA3AF",
                        fontSize: 13,
                      }}
                    >
                      No stage history available yet.
                    </div>
                  ) : (
                    stageHistories.map((stage, i) => (
                      <StageHistoryItem
                        key={stage.enquiryStageId || i}
                        stage={stage}
                        index={i}
                        isActive={
                          i === currentStageIndex ||
                          (currentStageIndex === -1 &&
                            i === stageHistories.length - 1)
                        }
                        isLast={i === stageHistories.length - 1}
                      />
                    ))
                  )}
                </div>
              )}

              {/* ─── ORDERS TAB ─── */}
              {activeTab === "orders" && (
                <div>
                  {purchaseOrders.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        paddingTop: 40,
                        color: "#9CA3AF",
                        fontSize: 13,
                      }}
                    >
                      No purchase orders yet.
                    </div>
                  ) : (
                    <>
                      {totalOrderValue > 0 && (
                        <div
                          style={{
                            background:
                              "linear-gradient(135deg, #6366F1, #8B5CF6)",
                            borderRadius: 12,
                            padding: "14px 16px",
                            marginBottom: 16,
                            color: "#fff",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              opacity: 0.8,
                              marginBottom: 4,
                            }}
                          >
                            Total Order Value
                          </div>
                          <div style={{ fontSize: 22, fontWeight: 800 }}>
                            ₹{totalOrderValue.toLocaleString("en-IN")}
                          </div>
                          <div
                            style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}
                          >
                            across {purchaseOrders.length} order
                            {purchaseOrders.length > 1 ? "s" : ""}
                          </div>
                        </div>
                      )}
                      {purchaseOrders.map((po, i) => (
                        <PurchaseOrderCard
                          key={po.purchaseOrderId || i}
                          po={po}
                        />
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* ─── OVERVIEW TAB ─── */}
              {activeTab === "overview" && (
                <div>
                  {(enquiry.lead || enquiry.client) && (
                    <Section title="Contact" icon="👤" accent="#6366F1">
                      <div
                        style={{
                          background: "#F9FAFB",
                          borderRadius: 10,
                          padding: "12px 14px",
                        }}
                      >
                        {enquiry.lead && (
                          <>
                            <div
                              style={{
                                fontSize: 12,
                                color: "#9CA3AF",
                                marginBottom: 6,
                                fontWeight: 600,
                              }}
                            >
                              LEAD
                            </div>
                            <InfoRow
                              label="Company"
                              value={enquiry.lead.companyName}
                            />
                            <InfoRow
                              label="Contact"
                              value={enquiry.lead.contactPerson}
                            />
                            <InfoRow label="Email" value={enquiry.lead.email} />
                            <InfoRow label="Phone" value={enquiry.lead.phone} />
                            <InfoRow
                              label="Code"
                              value={enquiry.lead.leadCode}
                            />
                          </>
                        )}
                        {enquiry.client && (
                          <>
                            <div
                              style={{
                                fontSize: 12,
                                color: "#9CA3AF",
                                marginTop: enquiry.lead ? 12 : 0,
                                marginBottom: 6,
                                fontWeight: 600,
                              }}
                            >
                              Client
                            </div>
                            <InfoRow
                              label="Company"
                              value={enquiry.client.companyName}
                            />
                            <InfoRow
                              label="Contact"
                              value={enquiry.client.contactPerson}
                            />
                            <InfoRow
                              label="Email"
                              value={enquiry.client.email}
                            />
                            <InfoRow
                              label="Phone"
                              value={enquiry.client.phone}
                            />
                          </>
                        )}
                      </div>
                    </Section>
                  )}

                  <Section title="Enquiry Details" icon="📋" accent="#8B5CF6">
                    <div
                      style={{
                        background: "#F9FAFB",
                        borderRadius: 10,
                        padding: "12px 14px",
                      }}
                    >
                      <InfoRow
                        label="Status"
                        value={
                          enquiry.status && (
                            <StatusBadge status={enquiry.status} />
                          )
                        }
                      />
                      <InfoRow label="Stage" value={enquiry.currentStageName} />
                      <InfoRow
                        label="Expected Close"
                        value={formatDateShort(enquiry.expectedCloseDate)}
                      />
                      <InfoRow
                        label="Closed On"
                        value={formatDateShort(enquiry.closedDate)}
                      />
                      <InfoRow
                        label="Discount"
                        value={
                          enquiry.discountPercentage
                            ? `${enquiry.discountPercentage}%`
                            : null
                        }
                      />
                      {enquiry.notes && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6B7280",
                            fontStyle: "italic",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "8px 10px",
                            marginTop: 8,
                            borderLeft: "3px solid #E5E7EB",
                          }}
                        >
                          {enquiry.notes}
                        </div>
                      )}
                    </div>
                  </Section>

                  <Section title="Financials" icon="💰" accent="#059669">
                    <div
                      style={{
                        background: "#F9FAFB",
                        borderRadius: 10,
                        padding: "12px 14px",
                      }}
                    >
                      <InfoRow
                        label="Estimate"
                        value={`₹${(enquiry.totalEstimateAmount || 0).toLocaleString("en-IN")}`}
                      />
                      <InfoRow
                        label="Quotation"
                        value={`₹${(enquiry.totalQuotationAmount || 0).toLocaleString("en-IN")}`}
                      />
                      {totalOrderValue > 0 && (
                        <InfoRow
                          label="Orders Total"
                          value={`₹${totalOrderValue.toLocaleString("en-IN")}`}
                        />
                      )}
                    </div>
                  </Section>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryTimelineSidebar;

export const EnquiryTimelineIcon = ({
  enquiryId,
  getApiEndpoint,
  tooltip = "View enquiry timeline",
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        title={tooltip}
        style={{
          width: 28,
          height: 28,
          borderRadius: 20,
          border: "1px solid #E5E7EB",
          background: "white",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          fontSize: 14,
          color: "black",
          transition: "all 0.15s",
          padding: "5px",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#EDE9FE";
          e.currentTarget.style.borderColor = "#A5B4FC";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.borderColor = "#E5E7EB";
        }}
        aria-label={tooltip}
      >
        ⏱
      </button>
      {open &&
        createPortal(
          <EnquiryTimelineSidebar
            enquiryId={enquiryId}
            getApiEndpoint={getApiEndpoint}
            onClose={() => setOpen(false)}
          />,
          document.body, // ← render into body, outside all containers
        )}
    </>
  );
};
